'use client';

import { useEffect, Suspense } from 'react';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import { useAuth } from '../contexts/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  redirectTo?: string;
  requireAuth?: boolean;
}

// Inner component that uses useSearchParams
function ProtectedRouteInner({
  children,
  redirectTo = '/',
  requireAuth = true,
}: ProtectedRouteProps) {
  const { user, loading } = useAuth();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (!loading) {
      if (requireAuth && !user) {
        // User is not authenticated, redirect to login
        router.push(redirectTo);
      } else if (!requireAuth && user) {
        // User is authenticated but on a public route, redirect to dashboard
        router.push('/landing-page');
      }
    }
  }, [user, loading, router, requireAuth, redirectTo, pathname, searchParams]);

  // Show loading state while checking authentication
  // if (loading) {
  //   return (
  //     <div style={{
  //       display: 'flex',
  //       justifyContent: 'center',
  //       alignItems: 'center',
  //       height: '100vh'
  //     }}>
  //       Loading...
  //     </div>
  //   );
  // }

  // If we require auth and user is not authenticated, don't render children
  if (requireAuth && !user) {
    return null;
  }

  // If we don't require auth and user is authenticated, don't render children
  if (!requireAuth && user) {
    return null;
  }

  // Render children if authentication state matches requirements
  return <>{children}</>;
}

// Wrapper component with Suspense boundary
export default function ProtectedRoute(props: ProtectedRouteProps) {
  return (
    <Suspense fallback={null}>
      <ProtectedRouteInner {...props} />
    </Suspense>
  );
}
